import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Files, 
  ChartPieSlice, 
  GraduationCap, 
  Clock,
  TrendUp,
  Warning,
  CheckCircle
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { 
  RadialBarChart, 
  RadialBar, 
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "UPLOAD": return <Files className="w-4 h-4 text-cyber-primary" />;
      case "ANALYZE": return <ChartPieSlice className="w-4 h-4 text-cyber-volt" />;
      case "EXAM_COMPLETED": return <GraduationCap className="w-4 h-4 text-cyber-success" />;
      default: return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-cyber-primary font-mono">Chargement...</div>
      </div>
    );
  }

  const complianceData = [
    { name: "Score", value: stats?.delta?.compliance_score || 0, fill: "#00E5FF" }
  ];

  const deltaChartData = [
    { name: "Conforme", value: stats?.delta?.conforme || 0, color: "#00FF66" },
    { name: "Partiel", value: stats?.delta?.partiel || 0, color: "#CCFF00" },
    { name: "Non conforme", value: stats?.delta?.non_conforme || 0, color: "#FF3B30" },
  ];

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading font-black text-3xl tracking-tight mb-2">
          Tableau de bord
        </h1>
        <p className="text-slate-400">
          Bienvenue, <span className="text-cyber-primary font-mono">{user?.name}</span>
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="cyber-card p-6"
          data-testid="stat-documents"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-400 mb-1">Documents</p>
              <p className="font-mono text-3xl text-white">{stats?.documents?.total || 0}</p>
              <p className="text-xs text-slate-500 mt-1">
                {stats?.documents?.analyzed || 0} analysés
              </p>
            </div>
            <div className="w-10 h-10 bg-cyber-primary/10 rounded-sm flex items-center justify-center">
              <Files weight="duotone" className="w-5 h-5 text-cyber-primary" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="cyber-card p-6"
          data-testid="stat-compliance"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-400 mb-1">Conformité Delta</p>
              <p className="font-mono text-3xl text-white">
                {stats?.delta?.compliance_score || 0}
                <span className="text-lg text-cyber-primary">%</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {stats?.delta?.total || 0} contrôles
              </p>
            </div>
            <div className="w-10 h-10 bg-cyber-volt/10 rounded-sm flex items-center justify-center">
              <TrendUp weight="duotone" className="w-5 h-5 text-cyber-volt" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="cyber-card p-6"
          data-testid="stat-exams"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-400 mb-1">Examens passés</p>
              <p className="font-mono text-3xl text-white">{stats?.exams?.total_attempts || 0}</p>
              <p className="text-xs text-slate-500 mt-1">
                Score moyen: {stats?.exams?.average_score || 0}%
              </p>
            </div>
            <div className="w-10 h-10 bg-cyber-success/10 rounded-sm flex items-center justify-center">
              <GraduationCap weight="duotone" className="w-5 h-5 text-cyber-success" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="cyber-card p-6"
          data-testid="stat-gaps"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-400 mb-1">Points d'attention</p>
              <p className="font-mono text-3xl text-white">
                {(stats?.delta?.non_conforme || 0) + (stats?.delta?.partiel || 0)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                À traiter pour certification
              </p>
            </div>
            <div className="w-10 h-10 bg-cyber-danger/10 rounded-sm flex items-center justify-center">
              <Warning weight="duotone" className="w-5 h-5 text-cyber-danger" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        {/* Compliance Score Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="cyber-card p-6"
          data-testid="chart-compliance"
        >
          <h3 className="font-heading font-bold text-lg mb-4">Score de conformité</h3>
          <div className="h-48 w-full relative">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart
                cx="50%"
                cy="50%"
                innerRadius="60%"
                outerRadius="90%"
                barSize={12}
                data={complianceData}
                startAngle={180}
                endAngle={0}
              >
                <RadialBar
                  background={{ fill: "#262D3D" }}
                  dataKey="value"
                  cornerRadius={6}
                />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-mono text-4xl text-cyber-primary">
                {stats?.delta?.compliance_score || 0}%
              </span>
              <span className="text-xs text-slate-400">Conformité</span>
            </div>
          </div>
        </motion.div>

        {/* Delta Status Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="cyber-card p-6 lg:col-span-2"
          data-testid="chart-delta"
        >
          <h3 className="font-heading font-bold text-lg mb-4">État des contrôles ISO 27001</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={deltaChartData} layout="vertical">
                <XAxis type="number" stroke="#262D3D" tick={{ fill: "#94A3B8", fontSize: 12 }} />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  stroke="#262D3D" 
                  tick={{ fill: "#94A3B8", fontSize: 12 }}
                  width={100}
                />
                <Tooltip
                  contentStyle={{
                    background: "#151A26",
                    border: "1px solid #262D3D",
                    borderRadius: "2px",
                  }}
                  labelStyle={{ color: "#fff" }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {deltaChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="cyber-card p-6"
        data-testid="recent-activity"
      >
        <h3 className="font-heading font-bold text-lg mb-4">Activité récente</h3>
        {stats?.recent_activity?.length > 0 ? (
          <div className="space-y-3">
            {stats.recent_activity.map((log, index) => (
              <div
                key={log.id || index}
                className="flex items-center gap-4 p-3 bg-cyber-surface-hover/50 rounded-sm border border-cyber-border"
              >
                <div className="w-8 h-8 bg-cyber-bg rounded-sm flex items-center justify-center">
                  {getActionIcon(log.action)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">
                    <span className="text-cyber-primary font-mono">{log.user_name}</span>
                    {" - "}
                    <span className="text-slate-300">{log.details}</span>
                  </p>
                  <p className="text-xs text-slate-500 font-mono">{formatTime(log.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <Clock weight="duotone" className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Aucune activité récente</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
